import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag } from "entities/shared/tags.types";
import { useRequestCallbacks } from "shared/api/lib/api.hooks";

import { tagsService } from "../tags.api";

type TagsRequestFunc = () => void;
type ResetTagsQueryFunc = () => Promise<void>;
type RemoveTagsCacheFunc = () => void;

type UseAirGetTagsQueryReturnType = {
  airTagsRequest: TagsRequestFunc;
  resetAirTagsQuery: ResetTagsQueryFunc;
  removeAirTagsCache: RemoveTagsCacheFunc;
  airTagsData: Tag[] | undefined;
  isAirTagsSuccess: boolean;
  isAirTagsError: boolean;
  isAirTagsLoading: boolean;
  isAirTagsFetching: boolean;
};

const TAGS_QUERY_KEY = "airTagsQuery";

export const useAirGetTagsQuery = (): UseAirGetTagsQueryReturnType => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean>(false);

  const meta = useQuery<Tag[], Error, Tag[]>({
    queryKey: [TAGS_QUERY_KEY],
    queryFn: tagsService.getTags,
    enabled,
  });

  const { data, isError, isLoading, isSuccess, isFetching } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<Tag[] | undefined, Error>(meta);

  const resetAirTagsQuery: ResetTagsQueryFunc = () => {
    return queryClient.resetQueries({ queryKey: [TAGS_QUERY_KEY] });
  };

  const removeAirTagsCache: RemoveTagsCacheFunc = () => {
    queryClient.removeQueries({ queryKey: [TAGS_QUERY_KEY] });
  };

  const airTagsRequest = useCallback<TagsRequestFunc>(() => {
    setRequestCallbacks({
      onSettled: () => {
        setEnabled(false);
      },
    });
    setEnabled(true);
  }, []);

  return {
    airTagsRequest,
    airTagsData: data,
    isAirTagsSuccess: isSuccess,
    isAirTagsError: isError,
    isAirTagsLoading: isLoading,
    isAirTagsFetching: isFetching,
    removeAirTagsCache,
    resetAirTagsQuery,
  };
};
